/**
 * AdminGuard Component
 *
 * Guards admin routes to ensure only superuser admin users can access
 * the admin console. Checks `is_superuser` flag in the current user's
 * profile before rendering the protected content.
 *
 * Usage:
 * ```tsx
 * <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
 * ```
 *
 * Redirects to `/dashboard` if the current user is authenticated but not
 * a superuser (see GitHub issue #498's "[permission]" QA case — updated
 * from an earlier inline "403 Forbidden" message that left the denied
 * admin content's route mounted rather than actually redirecting away).
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../app/providers';

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * AdminGuard component that wraps admin routes
 *
 * Checks if the current user is a superuser before allowing access.
 * If not authenticated, delegates to the parent ProtectedRoute.
 * If authenticated but not a superuser, redirects to `/dashboard`.
 */
export function AdminGuard({ children }: AdminGuardProps): React.ReactElement {
  const { user, isLoading } = useAuth();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">Checking permissions...</div>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we verify admin access
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect should happen at ProtectedRoute level
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Authentication Required</div>
          <p className="text-sm text-muted-foreground mt-2">
            You must be logged in to access the admin console
          </p>
        </div>
      </div>
    );
  }

  // Check if user is a superuser
  // TODO: Get is_superuser from user profile after user context is fully populated
  // For now, this is a placeholder that would need to read from user metadata
  const isSuperuser =
    user.user_metadata?.is_superuser === true || user.app_metadata?.is_superuser === true;

  // Not a superuser - deny access.
  //
  // GitHub issue #498 ("E2E: Sourcing & outreach") QA case ("[permission]
  // Given a logged-in non-superuser session, when the browser navigates
  // to /admin/jobs/:jobId, then access to the outreach management admin
  // view is denied (redirected away, table not rendered)") requires an
  // actual redirect away from the admin route, not just an inline 403
  // message left mounted at the same URL — `<Navigate replace>` here
  // (rather than the previous inline 403 markup) satisfies that: the
  // guarded admin content (`children`) never renders, and the browser
  // lands on `/dashboard` instead of staying on the denied admin URL.
  if (!isSuperuser) {
    return <Navigate to="/dashboard?admin_access_denied=1" replace />;
  }

  // User is authenticated and is a superuser - render children
  return <>{children}</>;
}

export default AdminGuard;
